export interface GeoData {
  country: string;
  countryCode: string;
  region: string;
  city: string;
  timezone: string;
  currency: string;
  isEU: boolean;
}

const CACHE_KEY = 'noteon_geo_data';
const CACHE_TTL = 3600000; // 1 hour

interface CachedGeoData {
  data: GeoData;
  timestamp: number;
}

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

export function getCachedGeoData(): GeoData | null {
  if (typeof window === 'undefined') return null;
  try {
    const cached = window.sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedGeoData = JSON.parse(cached);
    const now = Date.now();

    if (now - parsed.timestamp > CACHE_TTL) {
      window.sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
}

function setCachedGeoData(data: GeoData) {
  if (typeof window === 'undefined') return;
  try {
    const toCache: CachedGeoData = {
      data,
      timestamp: Date.now(),
    };
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(toCache));
  } catch {
    // Ignore storage errors
  }
}

function mapCurrency(countryCode: string): string {
  if (countryCode === 'PL') return 'PLN';
  if (countryCode === 'GB') return 'GBP';
  if (countryCode === 'US') return 'USD';
  if (countryCode === 'CH') return 'CHF';
  return 'EUR';
}

export async function fetchGeoData(): Promise<GeoData> {
  if (typeof window === 'undefined') {
    return {
      country: '',
      countryCode: '',
      region: '',
      city: '',
      timezone: '',
      currency: 'EUR',
      isEU: false,
    };
  }

  const cached = getCachedGeoData();
  if (cached) return cached;

  const response = await fetch('https://ipapi.co/json/', {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch geolocation');
  }

  const data = await response.json();

  const countryCode = data.country_code || '';
  const isEU = EU_COUNTRIES.includes(countryCode);
  const currency = mapCurrency(countryCode);

  const geoData: GeoData = {
    country: data.country_name || '',
    countryCode,
    region: data.region || '',
    city: data.city || '',
    timezone: data.timezone || '',
    currency,
    isEU,
  };

  setCachedGeoData(geoData);
  return geoData;
}
